import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-moment';

import { useTheme } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';

import fetchTimeout from '../fetchTimeout';
//import StopCount from './StopCount';

export default function DoubleChart({ day1, day2, value, unit, beginAtZero = true, stopDetails = true }) {
	const [data, setData] = useState({
		datasets: [
			{
				label: day1.label,
				fill: false,
				data: []
			},
			{
				label: day2.label,
				fill: false,
				data: []
			}
		]
	});
	const [fetchedData, setFetchedData] = useState();
	const [loading, setLoading] = useState(true);
	const theme = useTheme();

	const options = {
		maintainAspectRatio: false,
		scales: {
			y: {
				beginAtZero,
				ticks: {
					color: theme.palette.text.primary
				},
				grid: {
					color: theme.palette.divider
				}
			},
			x: {
				type: 'time',
				time: {
					displayFormats: {
						millisecond: 'H:mm:ss',
						second: 'H:mm:ss',
						minute: 'H:mm:ss',
						hour: 'H:mm:ss',
						day: 'H:mm:ss'
					}
				},
				ticks: {
					color: theme.palette.text.primary
				},
				grid: {
					color: theme.palette.divider
				}
			}
		},
		interaction: {
			intersect: false,
			mode: 'index',
			callbacks: {
				label: (a) => `${a.dataset.label}: ${a.raw.y} ${unit}`,
				title: (a) => `${new Date(a[0].raw.x).toLocaleTimeString()}`
			}
		},
		elements: {
			line: {
				tension: 0,
				width: 1
			},
			point: {
				radius: 0
			}
		},
		color: theme.palette.text.primary
	};

	useEffect(() => {
		function updateColors() {
			setData((oldData) => {
				if (oldData) {
					try {
						let ds1 = oldData.datasets[0];
						ds1.backgroundColor = theme.palette.primary.main;
						ds1.borderColor = theme.palette.primary.main;

						let ds2 = oldData.datasets[1];
						ds2.backgroundColor = theme.palette.secondary.main;
						ds2.borderColor = theme.palette.secondary.main;

						return { datasets: [ds1, ds2] };
					} catch (e) {
						console.log(e);
					}
				}
			});
		}

		async function fetchData() {
			setLoading(true);
			let d1t = day1.time;
			if (d1t === 'today') d1t = new Date().getTime();
			if (d1t === 'yesterday') d1t = new Date().getTime() - 24 * 60 * 60 * 1000;
			const day1Time = new Date(d1t);
			day1Time.setHours(0);
			day1Time.setMinutes(0);
			day1Time.setSeconds(0);
			day1Time.setMilliseconds(0);

			let d2t = day2.time;
			if (d2t === 'today') d2t = new Date().getTime();
			if (d2t === 'yesterday') d2t = new Date().getTime() - 24 * 60 * 60 * 1000;
			const day2Time = new Date(d2t);
			day2Time.setHours(0);
			day2Time.setMinutes(0);
			day2Time.setSeconds(0);
			day2Time.setMilliseconds(0);

			const offset = day1Time.getTime() - day2Time.getTime();

			const day1Response = await fetchTimeout(`api/solar/history/${day1Time.getFullYear()}/${day1Time.getMonth() + 1}/${day1Time.getDate()}`, {}, 5000);
			const day2Response = await fetchTimeout(`api/solar/history/${day2Time.getFullYear()}/${day2Time.getMonth() + 1}/${day2Time.getDate()}`, {}, 5000);

			const day1Data = await day1Response.json();
			const day2Data = await day2Response.json();

			let newData = {
				datasets: [
					{
						label: day1.label,
						fill: false,
						data: []
					},
					{
						label: day2.label,
						fill: false,
						data: []
					}
				]
			};

			let day1StartTime, day1EndTime;
			Object.keys(day1Data).forEach((time) => {
				time = parseInt(time);
				const val = day1Data[time]?.[value] ?? 0;
				if (val !== 0) {
					if (day1StartTime > time || day1StartTime === undefined) day1StartTime = time;
					if (day1EndTime < time || day1EndTime === undefined) day1EndTime = time;
				}
			});

			let day2StartTime, day2EndTime;
			Object.keys(day2Data).forEach((time) => {
				time = parseInt(time);
				const val = day2Data[time]?.[value] ?? 0;
				if (val !== 0) {
					if (day2StartTime > time || day2StartTime === undefined) day2StartTime = time;
					if (day2EndTime < time || day2EndTime === undefined) day2EndTime = time;
				}
			});
			day2StartTime += offset;
			day2EndTime += offset;

			let lim = Math.max(day1EndTime ?? day2EndTime, day2EndTime ?? day1EndTime);
			for (let i = Math.min(day1StartTime ?? day2StartTime, day2StartTime ?? day1StartTime); i < lim; i += 60000) {
				const val1 = day1Data[i]?.[value] ?? day1Data[i + 60000]?.[value] ?? 0;
				const val2 = day2Data[i - offset]?.[value] ?? day2Data[i - offset + 60000]?.[value] ?? 0;
				if (day1EndTime >= i) {
					newData.datasets[0].data.push({ x: i, y: val1 });
				}
				if (day2EndTime >= i) {
					newData.datasets[1].data.push({ x: i, y: val2 });
				}
			}
			setData(newData);
			setLoading(false);
			updateColors();
		}
		if (
			fetchedData?.day1?.label !== day1.label ||
			fetchedData?.day1?.time !== day1.time ||
			fetchedData?.day2?.label !== day2.label ||
			fetchedData?.day2?.time !== day2.time
		) {
			setFetchedData({ day1, day2 });
			fetchData();
		} else {
			updateColors();
		}
	}, [day1, day2, fetchedData, theme, value]);

	return (
		<Card
			elevation={3}
			sx={{
				minWidth: 300,
				flexGrow: 1,
				maxWidth: 1200,
				height: 'fit-content',
				margin: 1,
				minHeight: 500
			}}
		>
			{!loading ? (
				<CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
					<Line data={data} options={options} height={500} />
				</CardContent>
			) : (
				<CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
					<CircularProgress />
				</CardContent>
			)}
		</Card>
	);
}
